import React, { useState, useRef } from 'react';
import { Star, Download, RotateCcw, Plus, Camera, Users, BarChart3, Clock, Target, TrendingUp, FileVideo, User } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

// Simple UI Components
const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg border shadow-sm ${className}`}>{children}</div>
);

const CardHeader = ({ children }) => (
  <div className="p-6 pb-4">{children}</div>
);

const CardTitle = ({ children, className = "" }) => (
  <h3 className={`text-lg font-semibold ${className}`}>{children}</h3>
);

const CardContent = ({ children }) => (
  <div className="p-6 pt-0">{children}</div>
);

const Button = ({ children, onClick, className = "", variant = "default", size = "default", disabled = false }) => {
  const baseClasses = "inline-flex items-center justify-center rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";
  const variants = {
    default: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500",
    outline: "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-blue-500"
  };
  const sizes = {
    default: "px-4 py-2 text-sm",
    sm: "px-3 py-1.5 text-xs"
  };
  
  return (
    <button 
      onClick={onClick} 
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {children}
    </button>
  );
};

const Input = ({ className = "", ...props }) => (
  <input 
    className={`flex h-10 w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${className}`}
    {...props}
  />
);

const Textarea = ({ className = "", ...props }) => (
  <textarea 
    className={`flex min-h-[60px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${className}`}
    {...props}
  />
);

const WrestlingAnalysisPlatform = () => {
  const [activeTab, setActiveTab] = useState('assessment');
  const [videoUrl, setVideoUrl] = useState('');
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const videoRef = useRef(null);
  
  // Frame capture and drawing state
  const [capturedFrames, setCapturedFrames] = useState([]);
  const [showDrawingTool, setShowDrawingTool] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(null);
  const [drawingTool, setDrawingTool] = useState('arrow'); // arrow, circle, line, freehand
  const [drawingColor, setDrawingColor] = useState('#FF0000');
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingData, setDrawingData] = useState([]);
  const canvasRef = useRef(null);
  const [startPoint, setStartPoint] = useState(null);

  // Wrestler and Assessment Data
  const [wrestlerInfo, setWrestlerInfo] = useState({
    name: '',
    age: '',
    level: '',
    weight: '',
    team: '',
    date: new Date().toISOString().split('T')[0]
  });

  const [scores, setScores] = useState({
    neutral: { score: 0, notes: '', timestamps: [] },
    top: { score: 0, notes: '', timestamps: [] },
    bottom: { score: 0, notes: '', timestamps: [] },
    defense: { score: 0, notes: '', timestamps: [] },
    movement: { score: 0, notes: '', timestamps: [] },
    handFighting: { score: 0, notes: '', timestamps: [] }
  });

  const [matchSituation, setMatchSituation] = useState('');
  const [fatigueLevel, setFatigueLevel] = useState(1);
  const [overallNotes, setOverallNotes] = useState('');
  const [trainingGoals, setTrainingGoals] = useState('');
  
  // Client Management with localStorage
  const [clients, setClients] = useState(() => {
    const saved = localStorage.getItem('wrestling-clients');
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedClient, setSelectedClient] = useState(null);
  const [savedAssessments, setSavedAssessments] = useState(() => {
    const saved = localStorage.getItem('wrestling-assessments');
    return saved ? JSON.parse(saved) : [];
  });

  const pillars = {
    neutral: { name: 'Neutral Position', description: 'Stance, level changes, setups, shot timing, positioning', color: '#3B82F6' },
    top: { name: 'Top Position', description: 'Control, pressure, ride mechanics, scoring opportunities', color: '#EF4444' },
    bottom: { name: 'Bottom Position', description: 'Base, posture, escapes, scramble recovery, hip movement', color: '#10B981' },
    defense: { name: 'Defensive Fundamentals', description: 'Sprawl, hip position, counter-attacks, scramble defense', color: '#F59E0B' },
    movement: { name: 'Movement Quality', description: 'Footwork, balance, coordination, transition smoothness', color: '#8B5CF6' },
    handFighting: { name: 'Hand Fighting', description: 'Tie-ups, hand control, inside position, wrist control', color: '#EC4899' }
  };

  const scoreDescriptions = {
    1: 'Needs Significant Work',
    2: 'Below Average',
    3: 'Average/Developing',
    4: 'Above Average',
    5: 'Excellent/Elite'
  };

  // YouTube URL detection and conversion
  const isYouTubeUrl = (url) => {
    return url.includes('youtube.com') || url.includes('youtu.be');
  };

  const getYouTubeEmbedUrl = (url) => {
    let videoId = '';
    
    // Handle YouTube Shorts
    if (url.includes('youtube.com/shorts/')) {
      videoId = url.split('shorts/')[1]?.split('?')[0];
    }
    // Handle regular YouTube watch URLs
    else if (url.includes('youtube.com/watch')) {
      videoId = url.split('v=')[1]?.split('&')[0];
    }
    // Handle youtu.be short links
    else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    }
    
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  // Video Integration Functions
  const addTimestamp = (pillar, note = '') => {
    const timestamp = {
      time: currentTime,
      note: note || `${pillars[pillar].name} observation`,
      id: Date.now()
    };
    setScores(prev => ({
      ...prev,
      [pillar]: {
        ...prev[pillar],
        timestamps: [...prev[pillar].timestamps, timestamp]
      }
    }));
  };

  const jumpToTimestamp = (time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Playback control functions
  const changePlaybackRate = (rate) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
      setPlaybackRate(rate);
    }
  };

  const stepFrame = (direction) => {
    if (videoRef.current) {
      // Step forward or backward by approximately 1 frame (assuming 30fps = 0.033s per frame)
      videoRef.current.currentTime += direction * 0.033;
    }
  };

  const skipSeconds = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime += seconds;
    }
  };

  // Frame capture and drawing functions
  const captureFrame = () => {
    if (!videoRef.current) {
      alert('No video loaded');
      return;
    }
    
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 800;
    canvas.height = video.videoHeight || 450;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    const frameData = canvas.toDataURL('image/png');
    setCurrentFrame(frameData);
    setShowDrawingTool(true);
    setDrawingData([]);
    
    // Initialize the drawing canvas after state updates
    setTimeout(() => {
      if (canvasRef.current) {
        const drawCanvas = canvasRef.current;
        const drawCtx = drawCanvas.getContext('2d');
        const img = new Image();
        img.src = frameData;
        img.onload = () => {
          drawCtx.drawImage(img, 0, 0, drawCanvas.width, drawCanvas.height);
        };
      }
    }, 100);
  };

  const startDrawing = (e) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    setIsDrawing(true);
    setStartPoint({ x, y });
    
    if (drawingTool === 'freehand') {
      setDrawingData(prev => [...prev, {
        tool: 'freehand',
        color: drawingColor,
        points: [{ x, y }]
      }]);
    }
  };

  const draw = (e) => {
    if (!isDrawing || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    if (drawingTool === 'freehand') {
      setDrawingData(prev => {
        const newData = [...prev];
        const currentLine = newData[newData.length - 1];
        currentLine.points.push({ x, y });
        return newData;
      });
      redrawCanvas();
    }
  };

  const endDrawing = (e) => {
    if (!isDrawing || !canvasRef.current || !startPoint) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    if (drawingTool !== 'freehand') {
      setDrawingData(prev => [...prev, {
        tool: drawingTool,
        color: drawingColor,
        start: startPoint,
        end: { x, y }
      }]);
    }
    
    setIsDrawing(false);
    setStartPoint(null);
    redrawCanvas();
  };

  const redrawCanvas = () => {
    if (!canvasRef.current || !currentFrame) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.src = currentFrame;
    
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      drawingData.forEach(item => {
        ctx.strokeStyle = item.color;
        ctx.fillStyle = item.color;
        ctx.lineWidth = 3;
        
        if (item.tool === 'arrow') {
          drawArrow(ctx, item.start.x, item.start.y, item.end.x, item.end.y);
        } else if (item.tool === 'circle') {
          const radius = Math.sqrt(Math.pow(item.end.x - item.start.x, 2) + Math.pow(item.end.y - item.start.y, 2));
          ctx.beginPath();
          ctx.arc(item.start.x, item.start.y, radius, 0, 2 * Math.PI);
          ctx.stroke();
        } else if (item.tool === 'line') {
          ctx.beginPath();
          ctx.moveTo(item.start.x, item.start.y);
          ctx.lineTo(item.end.x, item.end.y);
          ctx.stroke();
        } else if (item.tool === 'freehand' && item.points.length > 1) {
          ctx.beginPath();
          ctx.moveTo(item.points[0].x, item.points[0].y);
          for (let i = 1; i < item.points.length; i++) {
            ctx.lineTo(item.points[i].x, item.points[i].y);
          }
          ctx.stroke();
        }
      });
    };
  };

  const drawArrow = (ctx, fromX, fromY, toX, toY) => {
    const headLength = 15;
    const angle = Math.atan2(toY - fromY, toX - fromX);
    
    ctx.beginPath();
    ctx.moveTo(fromX, fromY);
    ctx.lineTo(toX, toY);
    ctx.stroke();
    
    ctx.beginPath();
    ctx.moveTo(toX, toY);
    ctx.lineTo(toX - headLength * Math.cos(angle - Math.PI / 6), toY - headLength * Math.sin(angle - Math.PI / 6));
    ctx.moveTo(toX, toY);
    ctx.lineTo(toX - headLength * Math.cos(angle + Math.PI / 6), toY - headLength * Math.sin(angle + Math.PI / 6));
    ctx.stroke();
  };

  const saveAnnotatedFrame = () => {
    if (!canvasRef.current) return;
    
    const frameNote = prompt('Add a note for this annotated frame:');
    if (frameNote === null) return;
    
    const annotatedImage = canvasRef.current.toDataURL('image/png');
    const newFrame = {
      id: Date.now(),
      image: annotatedImage,
      note: frameNote,
      timestamp: formatTime(currentTime),
      time: currentTime
    };
    
    setCapturedFrames(prev => [...prev, newFrame]);
    setShowDrawingTool(false);
    setCurrentFrame(null);
    setDrawingData([]);
  };

  const clearDrawing = () => {
    setDrawingData([]);
    redrawCanvas();
  };

  const deleteFrame = (frameId) => {
    setCapturedFrames(prev => prev.filter(f => f.id !== frameId));
  };

  // Assessment Functions
  const handleScoreChange = (pillar, score) => {
    setScores(prev => ({
      ...prev,
      [pillar]: { ...prev[pillar], score: parseInt(score) }
    }));
  };

  const handleNotesChange = (pillar, notes) => {
    setScores(prev => ({
      ...prev,
      [pillar]: { ...prev[pillar], notes }
    }));
  };

  const calculateOverallScore = () => {
    const totalScore = Object.values(scores).reduce((sum, pillar) => sum + pillar.score, 0);
    return (totalScore / 6).toFixed(1);
  };

  const getOverallRating = () => {
    const avg = calculateOverallScore();
    if (avg >= 4.5) return 'Elite';
    if (avg >= 3.5) return 'Advanced';
    if (avg >= 2.5) return 'Developing';
    if (avg >= 1.5) return 'Needs Work';
    return 'Beginner';
  };

  // Client Management Functions
  const addClient = () => {
    if (!wrestlerInfo.name) return;
    const newClient = {
      id: Date.now(),
      ...wrestlerInfo,
      assessments: [],
      created: new Date().toISOString()
    };
    const updatedClients = [...clients, newClient];
    setClients(updatedClients);
    setSelectedClient(newClient);
    
    // Save to localStorage immediately
    localStorage.setItem('wrestling-clients', JSON.stringify(updatedClients));
  };

  const saveAssessment = () => {
    const assessment = {
      id: Date.now(),
      wrestlerInfo,
      scores,
      matchSituation,
      fatigueLevel,
      overallNotes,
      trainingGoals,
      videoUrl,
      overallScore: calculateOverallScore(),
      overallRating: getOverallRating(),
      timestamp: new Date().toLocaleString()
    };
    
    const updatedAssessments = [...savedAssessments, assessment];
    setSavedAssessments(updatedAssessments);
    
    // Save assessments to localStorage immediately
    localStorage.setItem('wrestling-assessments', JSON.stringify(updatedAssessments));
    
    if (selectedClient) {
      const updatedClients = clients.map(client => 
        client.id === selectedClient.id 
          ? { ...client, assessments: [...client.assessments, assessment] }
          : client
      );
      setClients(updatedClients);
      
      // Save updated clients to localStorage immediately
      localStorage.setItem('wrestling-clients', JSON.stringify(updatedClients));
    }
  };

  // Progress Tracking Data
  const getProgressData = () => {
    if (!selectedClient || !selectedClient.assessments) return [];
    return selectedClient.assessments.map((assessment, index) => ({
      session: `Session ${index + 1}`,
      overall: parseFloat(assessment.overallScore),
      neutral: assessment.scores.neutral.score,
      top: assessment.scores.top.score,
      bottom: assessment.scores.bottom.score,
      defense: assessment.scores.defense.score,
      movement: assessment.scores.movement.score,
      handFighting: assessment.scores.handFighting.score
    }));
  };

  const getRadarData = () => {
    return Object.entries(pillars).map(([key, pillar]) => ({
      pillar: pillar.name,
      score: scores[key].score,
      fullMark: 5
    }));
  };

  const resetForm = () => {
    setWrestlerInfo({ name: '', age: '', level: '', weight: '', team: '', date: new Date().toISOString().split('T')[0] });
    setScores({
      neutral: { score: 0, notes: '', timestamps: [] },
      top: { score: 0, notes: '', timestamps: [] },
      bottom: { score: 0, notes: '', timestamps: [] },
      defense: { score: 0, notes: '', timestamps: [] },
      movement: { score: 0, notes: '', timestamps: [] },
      handFighting: { score: 0, notes: '', timestamps: [] }
    });
    setOverallNotes('');
    setTrainingGoals('');
    setVideoUrl('');
  };

  const exportReport = () => {
    const assessment = savedAssessments[savedAssessments.length - 1];
    if (!assessment) return;

    const report = `
WRESTLING PERFORMANCE ANALYSIS REPORT

Wrestler: ${assessment.wrestlerInfo.name}
Age/Level: ${assessment.wrestlerInfo.age} - ${assessment.wrestlerInfo.level}
Weight: ${assessment.wrestlerInfo.weight}
Team: ${assessment.wrestlerInfo.team}
Date: ${assessment.wrestlerInfo.date}
Overall Score: ${assessment.overallScore}/5.0 (${assessment.overallRating})

FUNDAMENTAL PILLAR BREAKDOWN:
${Object.entries(assessment.scores).map(([key, data]) => 
  `${pillars[key].name}: ${data.score}/5 - ${scoreDescriptions[data.score] || 'Not Rated'}
  Notes: ${data.notes || 'No specific notes'}
  ${data.timestamps.length > 0 ? `Video Timestamps: ${data.timestamps.map(t => `${formatTime(t.time)} - ${t.note}`).join(', ')}` : ''}
`).join('\n')}

MATCH ANALYSIS:
Situation: ${assessment.matchSituation}
Fatigue Level: ${assessment.fatigueLevel}/5

COACH RECOMMENDATIONS:
${assessment.overallNotes}

TRAINING GOALS:
${assessment.trainingGoals}

Generated by Wrestling Analysis Platform - ${assessment.timestamp}
    `.trim();

    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${wrestlerInfo.name || 'wrestler'}_analysis_${wrestlerInfo.date}.txt`;
    a.click();
  };

  const renderVideoSection = () => (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileVideo className="h-5 w-5" />
          Video Integration
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Input
              placeholder="Enter YouTube URL or direct video file URL (MP4, WebM, etc.)"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              className="mb-4"
            />
            <p className="text-xs text-gray-500 mb-4">
              Supports: YouTube links, direct video files. Note: Advanced playback controls work only with direct video files.
            </p>
            {videoUrl && (
              <div className="bg-gray-100 p-4 rounded-lg">
                {isYouTubeUrl(videoUrl) ? (
                  <iframe
                    src={getYouTubeEmbedUrl(videoUrl)}
                    className="w-full rounded aspect-video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Wrestling Analysis Video"
                  />
                ) : (
                  <video
                    ref={videoRef}
                    src={videoUrl}
                    controls
                    className="w-full rounded"
                    onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
                  />
                )}
                
                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="h-4 w-4" />
                      {isYouTubeUrl(videoUrl) ? 'YouTube Video Loaded' : `Current Time: ${formatTime(currentTime)}`}
                    </div>
                    {!isYouTubeUrl(videoUrl) && (
                      <div className="text-sm font-medium text-blue-600">
                        Speed: {playbackRate}x
                      </div>
                    )}
                  </div>

                  {/* Playback Controls - Only for direct video files */}
                  {!isYouTubeUrl(videoUrl) && (
                    <div className="space-y-3 border-t pt-3">
                      <div>
                        <h5 className="text-sm font-semibold mb-2">Playback Speed</h5>
                        <div className="flex gap-2">
                          {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 2].map((rate) => (
                            <button
                              key={rate}
                              onClick={() => changePlaybackRate(rate)}
                              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                                playbackRate === rate
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              {rate}x
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h5 className="text-sm font-semibold mb-2">Frame Control</h5>
                        <div className="flex gap-2">
                          <button
                            onClick={() => stepFrame(-1)}
                            className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium transition-colors"
                          >
                            ← Frame Back
                          </button>
                          <button
                            onClick={() => stepFrame(1)}
                            className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium transition-colors"
                          >
                            Frame Forward →
                          </button>
                        </div>
                      </div>

                      <div>
                        <h5 className="text-sm font-semibold mb-2">Quick Skip</h5>
                        <div className="flex gap-2">
                          <button
                            onClick={() => skipSeconds(-5)}
                            className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium transition-colors"
                          >
                            ← 5s
                          </button>
                          <button
                            onClick={() => skipSeconds(-1)}
                            className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium transition-colors"
                          >
                            ← 1s
                          </button>
                          <button
                            onClick={() => skipSeconds(1)}
                            className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium transition-colors"
                          >
                            1s →
                          </button>
                          <button
                            onClick={() => skipSeconds(5)}
                            className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium transition-colors"
                          >
                            5s →
                          </button>
                        </div>
                      </div>
                      <div>
                        <h5 className="text-sm font-semibold mb-2">Frame Capture & Analysis</h5>
                        <button
                          onClick={captureFrame}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium transition-colors w-full"
                        >
                          📸 Capture & Annotate Frame
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div>
            <h4 className="font-semibold mb-3">Quick Timestamp Actions</h4>
            <p className="text-xs text-gray-500 mb-3">
              {isYouTubeUrl(videoUrl) ? 
                'Note: Manual timestamp entry recommended for YouTube videos' : 
                'Click to mark current video time'}
            </p>
            <div className="space-y-2">
              {Object.entries(pillars).map(([key, pillar]) => (
                <Button
                  key={key}
                  size="sm"
                  variant="outline"
                  onClick={() => addTimestamp(key)}
                  className="w-full justify-start text-xs"
                >
                  <Camera className="h-3 w-3 mr-2" />
                  Mark {pillar.name}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderAssessmentTab = () => (
    <div>
      {renderVideoSection()}
      
      {/* Drawing Tool Modal */}
      {showDrawingTool && currentFrame && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Annotate Frame</h3>
                <button onClick={() => setShowDrawingTool(false)} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
              </div>
              
              <div className="mb-4 flex gap-4 flex-wrap">
                <div>
                  <label className="block text-sm font-medium mb-2">Drawing Tool</label>
                  <div className="flex gap-2">
                    {[
                      { value: 'arrow', label: '→ Arrow' },
                      { value: 'circle', label: '○ Circle' },
                      { value: 'line', label: '— Line' },
                      { value: 'freehand', label: '✏️ Draw' }
                    ].map(tool => (
                      <button
                        key={tool.value}
                        onClick={() => setDrawingTool(tool.value)}
                        className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                          drawingTool === tool.value ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'
                        }`}
                      >
                        {tool.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Color</label>
                  <div className="flex gap-2">
                    {['#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#FFFFFF'].map(color => (
                      <button
                        key={color}
                        onClick={() => setDrawingColor(color)}
                        className={`w-8 h-8 rounded border-2 ${drawingColor === color ? 'border-black' : 'border-gray-300'}`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="relative bg-black rounded-lg overflow-hidden mb-4">
                <canvas
                  ref={canvasRef}
                  width={800}
                  height={450}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={endDrawing}
                  onMouseLeave={endDrawing}
                  className="w-full cursor-crosshair"
                  style={{ display: 'block' }}
                />
              </div>
              
              <div className="flex gap-3">
                <button onClick={saveAnnotatedFrame} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-medium">
                  Save Annotated Frame
                </button>
                <button onClick={clearDrawing} className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded font-medium">
                  Clear Drawings
                </button>
                <button onClick={() => setShowDrawingTool(false)} className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded font-medium">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Captured Frames Gallery */}
      {capturedFrames.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Captured & Annotated Frames ({capturedFrames.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {capturedFrames.map((frame) => (
                <div key={frame.id} className="border rounded-lg overflow-hidden">
                  <img src={frame.image} alt="Annotated frame" className="w-full" />
                  <div className="p-3">
                    <div className="text-xs text-gray-500 mb-1">@ {frame.timestamp}</div>
                    <div className="text-sm mb-2">{frame.note}</div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const link = document.createElement('a');
                          link.download = `frame-${frame.timestamp}.png`;
                          link.href = frame.image;
                          link.click();
                        }}
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs"
                      >
                        Download
                      </button>
                      <button
                        onClick={() => jumpToTimestamp(frame.time)}
                        className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs"
                      >
                        Jump to Time
                      </button>
                      <button
                        onClick={() => deleteFrame(frame.id)}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Wrestler Information */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Wrestler Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Input placeholder="Name" value={wrestlerInfo.name} onChange={(e) => setWrestlerInfo(prev => ({ ...prev, name: e.target.value }))} />
            <Input placeholder="Age" value={wrestlerInfo.age} onChange={(e) => setWrestlerInfo(prev => ({ ...prev, age: e.target.value }))} />
            <Input placeholder="Level" value={wrestlerInfo.level} onChange={(e) => setWrestlerInfo(prev => ({ ...prev, level: e.target.value }))} />
            <Input placeholder="Weight" value={wrestlerInfo.weight} onChange={(e) => setWrestlerInfo(prev => ({ ...prev, weight: e.target.value }))} />
            <Input placeholder="Team" value={wrestlerInfo.team} onChange={(e) => setWrestlerInfo(prev => ({ ...prev, team: e.target.value }))} />
            <Input type="date" value={wrestlerInfo.date} onChange={(e) => setWrestlerInfo(prev => ({ ...prev, date: e.target.value }))} />
          </div>
        </CardContent>
      </Card>

      {/* Performance Pillars */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {Object.entries(pillars).map(([key, pillar]) => (
          <Card key={key} className="h-full">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full`} style={{ backgroundColor: pillar.color }}></div>
                {pillar.name}
              </CardTitle>
              <p className="text-sm text-gray-600">{pillar.description}</p>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium">Score: {scores[key].score}/5</span>
                  <span className="text-xs text-gray-500">{scoreDescriptions[scores[key].score] || 'Not Rated'}</span>
                </div>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button key={rating} onClick={() => handleScoreChange(key, rating)} className={`p-1 ${scores[key].score >= rating ? 'text-yellow-400' : 'text-gray-300'} hover:text-yellow-500 transition-colors`}>
                      <Star size={20} fill="currentColor" />
                    </button>
                  ))}
                </div>
              </div>
              <Textarea placeholder="Specific observations and notes..." value={scores[key].notes} onChange={(e) => handleNotesChange(key, e.target.value)} rows={3} className="text-sm mb-3" />
              
              {/* Timestamps for this pillar */}
              {scores[key].timestamps.length > 0 && (
                <div className="border-t pt-3">
                  <h5 className="text-xs font-medium mb-2">Video Timestamps:</h5>
                  <div className="space-y-1">
                    {scores[key].timestamps.map((timestamp) => (
                      <button key={timestamp.id} onClick={() => jumpToTimestamp(timestamp.time)} className="block w-full text-left text-xs p-2 bg-gray-50 hover:bg-gray-100 rounded transition-colors">
                        <span className="font-mono">{formatTime(timestamp.time)}</span> - {timestamp.note}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Enhanced Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader>
            <CardTitle>Match Situation Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Match Situation</label>
                <Input placeholder="e.g., Leading by 2 in 3rd period" value={matchSituation} onChange={(e) => setMatchSituation(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Fatigue Level: {fatigueLevel}/5</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((level) => (
                    <button key={level} onClick={() => setFatigueLevel(level)} className={`p-2 w-8 h-8 rounded ${fatigueLevel >= level ? 'bg-red-400 text-white' : 'bg-gray-200'} hover:bg-red-500 transition-colors text-xs`}>
                      {level}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">1=Fresh, 5=Exhausted</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Current Performance Radar</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={getRadarData()}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="pillar" tick={{ fontSize: 10 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 5]} />
                  <Radar name="Score" dataKey="score" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.3} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Overall Assessment */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Overall Assessment & Training Goals</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="p-4 bg-blue-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">{calculateOverallScore()}/5.0</div>
              <div className="text-lg text-blue-800">{getOverallRating()}</div>
            </div>
            <div className="lg:col-span-2 space-y-4">
              <Textarea placeholder="Overall observations, priority areas for improvement..." value={overallNotes} onChange={(e) => setOverallNotes(e.target.value)} rows={3} />
              <Textarea placeholder="Specific training goals and recommendations..." value={trainingGoals} onChange={(e) => setTrainingGoals(e.target.value)} rows={3} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Button onClick={addClient} className="bg-blue-600 hover:bg-blue-700">
          <User className="mr-2 h-4 w-4" />
          Add as Client
        </Button>
        <Button onClick={saveAssessment} className="bg-green-600 hover:bg-green-700">
          <Plus className="mr-2 h-4 w-4" />
          Save Assessment
        </Button>
        <Button onClick={exportReport} variant="outline">
          <Download className="mr-2 h-4 w-4" />
          Export Report
        </Button>
        <Button onClick={resetForm} variant="outline">
          <RotateCcw className="mr-2 h-4 w-4" />
          Reset Form
        </Button>
      </div>
    </div>
  );

  const renderClientsTab = () => (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Client Management</h2>
        <Button onClick={addClient} disabled={!wrestlerInfo.name}>
          <Plus className="mr-2 h-4 w-4" />
          Add Current Wrestler as Client
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Client List</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {clients.map((client) => (
                <button key={client.id} onClick={() => setSelectedClient(client)} className={`w-full p-3 text-left rounded-lg border transition-colors ${selectedClient?.id === client.id ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50'}`}>
                  <div className="font-medium">{client.name}</div>
                  <div className="text-sm text-gray-600">{client.level} - {client.weight} lbs</div>
                  <div className="text-xs text-gray-500">{client.assessments?.length || 0} assessments</div>
                </button>
              ))}
              {clients.length === 0 && (
                <p className="text-gray-500 text-center py-8">No clients added yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {selectedClient && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>{selectedClient.name} - Profile</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div><span className="font-medium">Age:</span> {selectedClient.age}</div>
                  <div><span className="font-medium">Level:</span> {selectedClient.level}</div>
                  <div><span className="font-medium">Weight:</span> {selectedClient.weight} lbs</div>
                  <div><span className="font-medium">Team:</span> {selectedClient.team}</div>
                  <div><span className="font-medium">Total Assessments:</span> {selectedClient.assessments?.length || 0}</div>
                  <div><span className="font-medium">Latest Score:</span> {selectedClient.assessments?.length > 0 ? `${selectedClient.assessments[selectedClient.assessments.length - 1].overallScore}/5.0` : 'No assessments'}</div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Recent Assessments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {selectedClient.assessments?.slice(-5).reverse().map((assessment) => (
                    <div key={assessment.id} className="p-3 border rounded-lg bg-gray-50">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{assessment.overallScore}/5.0</span>
                        <span className="text-xs text-gray-500">{new Date(assessment.timestamp).toLocaleDateString()}</span>
                      </div>
                      <div className="text-xs text-gray-600 mt-1">{assessment.overallRating}</div>
                    </div>
                  )) || <p className="text-gray-500 text-center py-4">No assessments yet</p>}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );

  const renderAnalyticsTab = () => (
    <div>
      <h2 className="text-2xl font-bold mb-6">Enhanced Analysis & Progress Tracking</h2>
      
      {selectedClient && selectedClient.assessments?.length > 0 ? (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Progress Over Time - {selectedClient.name}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={getProgressData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="session" />
                    <YAxis domain={[0, 5]} />
                    <Tooltip />
                    <Line type="monotone" dataKey="overall" stroke="#3B82F6" strokeWidth={3} name="Overall" />
                    <Line type="monotone" dataKey="neutral" stroke="#EF4444" name="Neutral" />
                    <Line type="monotone" dataKey="top" stroke="#10B981" name="Top" />
                    <Line type="monotone" dataKey="bottom" stroke="#F59E0B" name="Bottom" />
                    <Line type="monotone" dataKey="defense" stroke="#8B5CF6" name="Defense" />
                    <Line type="monotone" dataKey="movement" stroke="#EC4899" name="Movement" />
                    <Line type="monotone" dataKey="handFighting" stroke="#6B7280" name="Hand Fighting" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Improvement Trends</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(pillars).map(([key, pillar]) => {
                  const progressData = getProgressData();
                  const improvement = progressData.length > 1 ? 
                    progressData[progressData.length - 1][key] - progressData[0][key] : 0;
                  return (
                    <div key={key} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full`} style={{ backgroundColor: pillar.color }}></div>
                        <span className="font-medium">{pillar.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <TrendingUp className={`h-4 w-4 ${improvement > 0 ? 'text-green-600' : improvement < 0 ? 'text-red-600' : 'text-gray-400'}`} />
                        <span className={`font-medium ${improvement > 0 ? 'text-green-600' : improvement < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                          {improvement > 0 ? '+' : ''}{improvement.toFixed(1)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card>
          <CardContent className="text-center py-12">
            <BarChart3 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Analysis Data Available</h3>
            <p className="text-gray-600 mb-4">Select a client with multiple assessments to view progress analytics.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );

  return (
    <div className="wrestling-analysis-container">
      <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Wrestling Analysis Platform</h1>
          <p className="text-gray-600">Professional video analysis & performance tracking system</p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex space-x-1 mb-6 bg-white rounded-lg p-1">
          <button
            onClick={() => setActiveTab('assessment')}
            className={`px-6 py-2 rounded-md font-medium transition-colors ${
              activeTab === 'assessment' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Target className="inline mr-2 h-4 w-4" />
            Assessment
          </button>
          <button
            onClick={() => setActiveTab('clients')}
            className={`px-6 py-2 rounded-md font-medium transition-colors ${
              activeTab === 'clients' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Users className="inline mr-2 h-4 w-4" />
            Clients
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-6 py-2 rounded-md font-medium transition-colors ${
              activeTab === 'analytics' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <BarChart3 className="inline mr-2 h-4 w-4" />
            Analytics
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'assessment' && renderAssessmentTab()}
        {activeTab === 'clients' && renderClientsTab()}
        {activeTab === 'analytics' && renderAnalyticsTab()}
      </div>
    </div>
  );
};

export default WrestlingAnalysisPlatform;


